import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  BookOpen,
  CalendarDays,
  Check,
  ChevronRight,
  ClipboardList,
  Download,
  Dumbbell,
  Lock,
  ShoppingCart,
  TrendingUp,
  UtensilsCrossed,
  Zap,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { DownloadsPanel } from "@/components/kit/DownloadsPanel";
import { NoAccess } from "@/components/NoAccess";
import { ProgramHomeSkeleton } from "@/components/kit/Skeleton";
import { SLOT_LABELS, api, routes } from "@/lib/ppcp";
import { track } from "@/lib/analytics";
import { useProgress } from "@/lib/progress";
import { cn } from "@/lib/utils";
import { KIT, PAGES } from "@/constants/testIds";

const FEATURE_COPY = {
  protein_engine: {
    title: "High-Protein Companion",
    description:
      "The Protein Engine: your targets, portions and smart adjustments — with the right suggestion shown under each meal as you read.",
    guide: "protein",
    icon: Dumbbell,
  },
  grocery_engine: {
    title: "Grocery Guide",
    description:
      "Monthly staples plus the fresh list for the week you're on, with quantities for your household.",
    guide: "grocery",
    icon: ClipboardList,
  },
};

const NUMBER_WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];

/** One row of the "Your plan" list: peach icon badge, title, one-line subtitle, chevron. */
const PlanRowLink = ({ testId, icon: Icon, title, subtitle, onClick }) => (
  <li>
    <button
      data-testid={testId}
      onClick={onClick}
      className="ps-tap flex w-full items-center gap-3.5 py-4 text-left"
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-peach">
        <Icon className="h-[18px] w-[18px] text-brand-orange" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-display text-[15.5px] font-bold text-brand-teal">{title}</span>
        <span className="mt-0.5 block text-[13px] leading-[1.45] text-muted-foreground">{subtitle}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-brand-teal/30" />
    </button>
  </li>
);

const SectionTitle = ({ children, note }) => (
  <header className="mb-4">
    <h2 className="ps-display text-[19px] font-bold sm:text-[21px]">{children}</h2>
    {note && <p className="mt-1.5 text-[13.5px] leading-[1.6] text-brand-teal/60">{note}</p>}
  </header>
);

export default function ProgramHome() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pack, setPack] = useState(null);
  const [pdfsOpen, setPdfsOpen] = useState(false);
  const [denied, setDenied] = useState(false);
  const { summary } = useProgress();
  const reported = useRef(null);

  useEffect(() => {
    api
      .get(`/programs/${slug}`)
      .then((res) => {
        setPack(res.data);
        // One event per plan opened, however many times the effect runs.
        if (reported.current !== slug) {
          reported.current = slug;
          track("plan_opened", {
            plan_name: res.data?.program?.title,
            plan_slug: slug,
            tier: res.data?.tier,
          });
        }
      })
      .catch((err) => (err.response?.status === 403 ? setDenied(true) : setPack(false)));
  }, [slug]);

  if (denied) return <NoAccess />;

  // The plan's shape is still arriving: show its shape rather than an empty page.
  if (pack === null)
    return (
      <AppShell
        title="Program"
        slug={slug}
        crumbs={[{ label: "My Plans", to: routes.library }, { label: "Program" }]}
      >
        <ProgramHomeSkeleton />
      </AppShell>
    );

  const program = pack?.program;
  const isBundle = pack?.tier === "bundle";
  // Live progress from the shared store; the pack payload is only the initial fallback.
  const progress = summary ?? pack?.progress;
  // Each plan ships its own number of chapters — say the real count, never a hard-coded one.
  const count = program?.intro_pages?.length ?? 0;
  const chapterCount = NUMBER_WORDS[count] ?? count;
  const chapterNoun = count === 1 ? "chapter" : "chapters";
  // The meals a day actually asks you to tick: the plan's slots minus its optional snack slots.
  const optionalSlots = (program?.daily_snacks ?? []).filter((s) => s.required === false).length;
  const mealsPerDay = Math.max((program?.slots?.length ?? 0) - optionalSlots, 0);
  const resumeDay = progress?.started && !progress?.finished ? progress.resume_day : null;
  const done = progress?.days_complete ?? 0;
  const total = program?.duration_days ?? 0;

  const ctaLabel = progress?.finished
    ? "Start again from Day 1"
    : resumeDay
      ? `Continue Day ${resumeDay}`
      : "Start Day 1";
  const ctaNote = progress?.finished
    ? `You finished all ${total} days`
    : resumeDay
      ? `${done} of ${total} days done so far`
      : "Begin your journey today";
  const ctaDay = progress?.finished ? 1 : (resumeDay ?? 1);
  // A reader who has not ticked a single meal and has no completed day is still at the start.
  const isNewReader = !progress?.started && done === 0;
  const glanceMeals = (pack?.days ?? []).find((d) => d.day_number === ctaDay)?.meals ?? [];
  const groceryGuide = program?.guides?.find((g) => g.feature === "grocery_engine");
  const hasProteinBoost = (program?.bundle_features ?? []).includes("protein_engine");

  return (
    <AppShell
      title={program?.title ?? "Program"}
      slug={slug}
      todayHref={routes.day(slug, resumeDay ?? 1)}
      crumbs={[
        { label: "My Plans", to: routes.library },
        { label: program?.title ?? "Program" },
      ]}
    >
      <div data-testid={PAGES.programHome} className="contents">
        {/* A light header: where I am, and the one thing to do next. */}
        <header className="space-y-3 pb-1 pt-1">
          <p className="text-[11.5px] font-bold uppercase tracking-[0.1em] text-brand-orange">
            {isNewReader ? "Welcome" : program?.title}
          </p>
          <h1 className="font-display text-3xl font-semibold leading-tight text-brand-teal sm:text-4xl">
            {isNewReader ? (
              <>Start Day <span className="text-brand-orange">1</span></>
            ) : progress?.finished ? (
              <>All <span className="text-brand-orange">{total} days</span> done</>
            ) : (
              <>Day <span className="text-brand-orange">{ctaDay}</span> is next</>
            )}
          </h1>
          <div className="h-[5px] w-full overflow-hidden rounded-full bg-[#EEE9DE]">
            <div
              className="h-full rounded-full transition-[width] duration-700"
              style={{ width: `${progress?.percent ?? 0}%`, backgroundColor: "#DD4115" }}
            />
          </div>
          <p data-testid="program-progress-line" className="text-[14px] text-muted-foreground">
            {done} of {total} days done
          </p>
          {pack?.tier && (
            <p data-testid="program-tier-badge" className="text-[12px] font-bold text-brand-tealSoft">
              {isBundle ? "Complete Meal Plan" : "Meal Plan"} · Indian vegetarian
            </p>
          )}
        </header>

        {/* The one main action of the screen. */}
        {!isNewReader && (
          <button
            data-testid={KIT.resumeCta}
            onClick={() => navigate(routes.day(slug, ctaDay))}
            className="ps-tap flex min-h-[56px] w-full items-center justify-center rounded-full bg-brand-teal px-6 text-[15.5px] font-bold text-white transition-colors hover:bg-brand-tealDeep"
          >
            {ctaLabel}
          </button>
        )}

        {/* A reader who has not started yet gets the two steps, the button inside the card. */}
        {isNewReader && !!program?.intro_pages?.length && (
          <section className="ps-card ps-card-pad space-y-4">
            <p className="text-[11.5px] font-bold uppercase tracking-[0.1em] text-brand-orange">Start here</p>
            <ol className="divide-y divide-border">
              <li>
                <button
                  data-testid="start-here-chapters"
                  onClick={() => navigate(routes.intro(slug, program.intro_pages[0].slug))}
                  className="ps-tap flex w-full items-center gap-4 py-4 text-left"
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-peach font-display text-[14px] font-bold text-brand-orange">
                    1
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display text-[16px] font-bold text-brand-teal">
                      Read the {chapterCount} short {chapterNoun}
                    </span>
                    <span className="mt-0.5 block text-[13.5px] text-muted-foreground">
                      About 5 minutes. How the plan works.
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-brand-teal/30" />
                </button>
              </li>
              <li className="flex items-center gap-4 py-4">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-peach font-display text-[14px] font-bold text-brand-orange">
                  2
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-display text-[16px] font-bold text-brand-teal">Start Day 1</span>
                  <span className="mt-0.5 block text-[13.5px] text-muted-foreground">
                    {mealsPerDay} easy meals to begin
                  </span>
                </span>
              </li>
            </ol>
            <button
              data-testid={KIT.resumeCta}
              onClick={() => navigate(routes.day(slug, 1))}
              className="ps-tap flex min-h-[52px] w-full items-center justify-center rounded-full bg-brand-teal px-6 text-[15.5px] font-bold text-white transition-colors hover:bg-brand-tealDeep"
            >
              Start Day 1
            </button>
          </section>
        )}

        {/* The day at a glance, from the same day index the Day Index page reads. */}
        {!!glanceMeals.length && (
          <section className="ps-card ps-card-pad space-y-3">
            <h2 className="font-display text-[19px] font-bold text-brand-teal sm:text-[21px]">
              Day {ctaDay} at a glance
            </h2>
            <ul className="divide-y divide-border">
              {glanceMeals.map((meal) => (
                <li key={meal.slot} className="flex items-center gap-3.5 py-3">
                  <span className="ps-photo block h-[56px] w-[56px] shrink-0 rounded-[14px]">
                    {meal.image ? (
                      <img src={meal.image} alt="" loading="lazy" decoding="async" />
                    ) : (
                      <span className="grid h-full w-full place-items-center bg-secondary font-display text-[13px] font-bold text-brand-teal/50">
                        {meal.title?.[0]}
                      </span>
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[12.5px] text-muted-foreground">
                      {SLOT_LABELS[meal.slot] ?? meal.slot}
                    </span>
                    <span className="block font-display text-[15.5px] font-bold leading-snug text-brand-teal">
                      {meal.title}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Everything else lives in one quiet list. */}
        <section className="ps-card ps-card-pad">
          <h2 className="font-display text-[19px] font-bold text-brand-teal sm:text-[21px]">
            Your <span className="text-brand-orange">plan</span>
          </h2>
          <ul className="mt-2 divide-y divide-border">
            <PlanRowLink
              testId="plan-row-days"
              icon={CalendarDays}
              title="Day Index"
              subtitle={`See all ${total} days`}
              onClick={() => navigate(routes.days(slug))}
            />
            {isBundle && groceryGuide && (
              <PlanRowLink
                testId={KIT.guideLink("grocery")}
                icon={ShoppingCart}
                title={groceryGuide.title}
                subtitle="Shopping list for every week"
                onClick={() => navigate(routes.guide(slug, "grocery"))}
              />
            )}
            {!!program?.intro_pages?.length && (
              <PlanRowLink
                testId={KIT.introLink(program.intro_pages[0].slug)}
                icon={BookOpen}
                title="Chapters"
                subtitle={`${chapterCount} short ${chapterNoun} · optional`}
                onClick={() => navigate(routes.intro(slug, program.intro_pages[0].slug))}
              />
            )}
            <PlanRowLink
              testId="plan-row-pdfs"
              icon={Download}
              title="Printable PDFs"
              subtitle="Plan, grocery guide and fridge chart"
              onClick={() => setPdfsOpen((open) => !open)}
            />
            {isBundle && hasProteinBoost && (
              <PlanRowLink
                testId="plan-row-protein"
                icon={Zap}
                title={program.protein_label ?? "Protein Boost"}
                subtitle="Shown under every meal"
                onClick={() => navigate(routes.day(slug, ctaDay))}
              />
            )}
          </ul>
        </section>

        {/* The PDF list itself, with its existing download buttons, opened from the row above. */}
        {pdfsOpen && (
          <DownloadsPanel slug={slug} upgradeUrl={pack?.native_upgrade_url || pack?.upgrade_url} nativeUpgrade={!!pack?.native_upgrade_url} />
        )}

        {/* Complete-only guidance: an Essential reader still sees what it adds. A Complete reader
            reaches it from the "Your plan" list, so it is not repeated here. */}
        {!isBundle && !!program?.bundle_features?.length && (
          <section className="ps-card ps-card-pad">
            <p className="text-[11.5px] font-bold uppercase tracking-[0.09em] text-brand-orange">
              {isBundle ? "Included in your Complete Meal Plan" : "Part of the Complete Meal Plan"}
            </p>
            <div className="mt-2.5">
              <SectionTitle note={isBundle ? "These appear inside your daily meals, right where they're useful, while also having separate sections where you can explore them in more detail." : undefined}>
                {isBundle ? "Your extra guidance" : "Add with Complete"}
              </SectionTitle>
            </div>

            <div className={cn("mt-1", isBundle ? "grid gap-3 min-[481px]:grid-cols-2" : "divide-y divide-border")}>
              {program.bundle_features.map((key) => {
                const copy = FEATURE_COPY[key];
                const Icon = copy.icon;
                const guide = program.guides?.find((g) => g.feature === key);
                // Each plan names its own guide (the PCOS plan calls it the Grocery Companion). A plan
                // that ships no guide for a feature shows the feature's own name and does not link.
                const title = guide?.title ?? (key === "protein_engine" ? "Protein Boost" : copy.title);
                const clickable = isBundle && copy.guide && guide;
                const Tag = clickable ? "button" : "div";
                const description =
                  key === "protein_engine" && program.protein_label === "Protein Boost"
                    ? "Two simple ways to add protein, shown under every meal."
                    : copy.description;

                // An Essential reader sees a plain locked row, not a card inside a card.
                if (!isBundle) {
                  const upgradeHref = pack?.native_upgrade_url || pack?.upgrade_url;
                  const Row = upgradeHref ? "a" : "div";
                  return (
                    <Row
                      key={key}
                      {...(upgradeHref
                        ? {
                            href: upgradeHref,
                            target: "_blank",
                            rel: "noopener",
                            "data-testid": `upgrade-${key}`,
                          }
                        : { "data-testid": `bundle-feature-${key}` })}
                      className="flex items-start gap-4 py-4 first:pt-2"
                    >
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-secondary">
                        <Lock className="h-4 w-4 text-brand-tealSoft" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-display text-[15.5px] font-bold text-brand-teal">{title}</h3>
                        <p className="mt-1 text-[13px] leading-[1.5] text-muted-foreground">{description}</p>
                      </div>
                    </Row>
                  );
                }

                return (
                  <Tag
                    key={key}
                    {...(clickable
                      ? {
                          "data-testid": KIT.guideLink(copy.guide),
                          onClick: () => navigate(routes.guide(slug, copy.guide)),
                        }
                      : { "data-testid": `bundle-feature-${key}` })}
                    className={cn(
                      "rounded-[16px] bg-secondary/60 p-[17px] text-left",
                      clickable && "ps-tap group hover:bg-secondary"
                    )}
                  >
                    <span className="mb-[11px] grid h-8 w-8 place-items-center rounded-full bg-white">
                      <Icon className="h-[17px] w-[17px] text-brand-green" />
                    </span>
                    <h3 className="mt-3 flex items-center gap-2 font-display text-[14.5px] font-bold text-brand-teal">
                      {title}
                      {clickable && (
                        <ArrowRight className="h-4 w-4 text-brand-green transition-transform duration-300 group-hover:translate-x-1" />
                      )}
                    </h3>
                    <p className="mt-1.5 text-[12.5px] leading-[1.5] text-muted-foreground">{description}</p>
                  </Tag>
                );
              })}
            </div>

            {!isBundle && (pack?.native_upgrade_url || pack?.upgrade_url) && (
              <a
                href={pack.native_upgrade_url || pack.upgrade_url}
                target="_blank"
                rel="noopener"
                data-testid="upgrade-complete"
                className="ps-tap mt-5 flex min-h-[48px] w-full items-center justify-center rounded-full bg-brand-peach px-5 text-[14px] font-bold text-brand-teal transition-colors hover:bg-brand-mint"
              >
                See what Complete adds
              </a>
            )}
          </section>
        )}
      </div>
    </AppShell>
  );
}
